/**
 * Podklad pod mape zasadnicza.
 *
 * Domyslnie OpenStreetMap. Sam rysunek mapy zasadniczej jest kompletny bez
 * podkladu - pochodzi w calosci z wczytanego GML-a - ale nie mowi, GDZIE to
 * jest. Podklad odpowiada na to od pierwszego otwarcia, bez szukania
 * przelacznika.
 *
 * Cena: podklad to jedyne miejsce, w ktorym przegladarka odzywa sie do obcego
 * serwera. Do OSM ida wylacznie numery kafli - nic z wczytanego GML-a nie
 * opuszcza serwisu. Wybor 'brak' wylacza to zupelnie i jest pamietany miedzy
 * sesjami, wiec raz wylaczony podklad nie wraca.
 *
 * Przy wiekszym ruchu wypada przejsc na wlasny serwer kafli albo platnego
 * dostawce - tile.openstreetmap.org to zasob fundacji, utrzymywany z darowizn,
 * a jego regulamin nie przewiduje ruchu z serwisow produkcyjnych.
 *
 * Ortofotomapa idzie przez WMS w wersji 1.1.1, nie 1.3.0. W 1.3.0 kolejnosc
 * osi bierze sie z definicji EPSG, a PL-2000 ma ja odwrotna (polnoc przed
 * wschodem) - sprawdzone na serwerze GUGiK: to samo zapytanie z zamienionymi
 * osiami zwraca bajt w bajt ten sam obraz, co 1.1.1. Wersja 1.1.1 ma kolejnosc
 * ustalona na wschod-polnoc i nie ma sie o co potykac.
 *
 * OSM siedzi w Web Mercatorze, wiec kafle przelicza przegladarka. Obraz jest
 * przez to nieco miekszy - to cena za uklad mapy, ktorego OSM nie podaje.
 * Przeliczanie wymaga, zeby uklad docelowy znal swoj zasieg; ustawia go
 * ensureProjections() w proj.ts.
 */
import ImageLayer from 'ol/layer/Image'
import TileLayer from 'ol/layer/Tile'
import type BaseLayer from 'ol/layer/Base'
import type Map from 'ol/Map'
import ImageWMS from 'ol/source/ImageWMS'
import OSM from 'ol/source/OSM'
import type { Projection } from 'ol/proj'

export type Podklad = 'brak' | 'orto' | 'osm'

export const PODKLADY: { id: Podklad; nazwa: string }[] = [
  { id: 'brak', nazwa: 'brak' },
  { id: 'orto', nazwa: 'ortofotomapa' },
  { id: 'osm', nazwa: 'OpenStreetMap' },
]

const ORTO_WMS = 'https://mapy.geoportal.gov.pl/wss/service/PZGIK/ORTO/WMS/StandardResolution'

function warstwa(kind: Podklad, projection: Projection): BaseLayer | null {
  if (kind === 'orto') {
    // JEDEN obraz na widok, a nie siatka kafli. Serwis GUGiK ogranicza tempo
    // zapytan - sprawdzone: cztery przechodza, piate w tej samej chwili wraca
    // z 404. Widok pokryty kaflami 256 px to kilkanascie zapytan naraz, wiec
    // podklad rozsypywalby sie dziurami akurat przy szybkim przesuwaniu mapy.
    // Jedno zapytanie na kadr miesci sie w limicie z zapasem.
    const src = new ImageWMS({
      url: ORTO_WMS,
      // TRANSPARENT wprost na false: OpenLayers domyslnie wysyla true, a to
      // przy JPEG-u nie ma sensu - format nie ma kanalu przezroczystosci.
      // WMS 1.1.1, nie 1.3.0 - patrz naglowek modulu.
      params: { LAYERS: 'Raster', VERSION: '1.1.1', FORMAT: 'image/jpeg',
                TRANSPARENT: false },
      projection,
      ratio: 1.2,
      attributions: 'Ortofotomapa: <a href="https://www.geoportal.gov.pl" '
                  + 'target="_blank" rel="noopener">GUGiK</a>',
    })

    // Odrzucone przez limit zapytanie wraca jako 404, czyli dla przegladarki
    // jako blad wczytania obrazu - i podklad po prostu znika az do nastepnego
    // ruchu mapa. Wyglada to jak awaria, wiec probujemy jeszcze dwa razy,
    // z narastajaca przerwa. Licznik zeruje sie po udanym wczytaniu, zeby
    // ponowki nie sumowaly sie przez cala sesje.
    let ponowka = 0
    src.on('imageloaderror', () => {
      if (ponowka >= 2) return
      ponowka += 1
      setTimeout(() => src.refresh(), 1200 * ponowka)
    })
    src.on('imageloadend', () => { ponowka = 0 })

    return new ImageLayer({ source: src })
  }
  if (kind === 'osm') return new TileLayer({ source: new OSM() })
  return null
}

/**
 * Podmienia podklad pod mapa. Warstwa podkladu stoi zawsze na pozycji 0,
 * czyli pod wypieczonym rysunkiem - inaczej przykrylaby mape zasadnicza.
 */
export function setBasemap(map: Map, kind: Podklad, projection: Projection): void {
  const layers = map.getLayers()
  const stary = layers.getArray().find(l => l.get('podklad') === true)
  if (stary) layers.remove(stary)

  const nowy = warstwa(kind, projection)
  if (!nowy) return
  nowy.set('podklad', true)
  layers.insertAt(0, nowy)
}
